/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';

import AppNavTopLevel from '../../components/appNav/AppNavTopLevel';
import TopLevelAddButton from '../../appAuthorized/presenters/TopLevelAddButton';
import ContentLoader from '../../components/ContentLoader';
import ShareCardModel, { ShareCard } from '../../../models/ShareCardModel';
import { ScrollAwareContext, ScrollAwareScroller } from '../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../components/appNav/AppHeaderScrollable';

import './Share.styles.scss';
import './Share.styles-mobile.scss';

import { ShareCardItem } from './ShareCardItem';
import { GrayTextMarkdown } from '../../components/Markdown';
import ShareCardModal from './ShareCardModal';
import ContentWrapper from '../../components/ContentWrapper';
import { TrackEvent } from '../../../utilities/UserMetrics';
import { Card } from '../../../bphComponents/bundle';
import { GeneralPrivacyHeader } from '../../components/PrivacyHeader';
import COLORS from '../../styles/Colors';

import AppNavPhrases from '../../../phraseKeys/AppNav.phrase';
import RouteNames from '../../appAuthorized/RouteNames';
import AppUtils from '../../../utilities/AppUtils';

import { useActiveUser } from '../../hooks/useUser';
import { useUIState } from '../../hooks/useUIState';
import { useActiveProfile } from '../../hooks/useProfile';
import { useNavigate } from '../../hooks/useNavigate';
import { useShareCardListForActiveProfile } from '../../hooks/useShareCard';
import { useHealthEntriesForActiveProfile } from '../../hooks/useHealthEntry';
import { useGroupShareCardContentForActiveProfile } from '../../hooks/useGroup';
import { useAttachmentsForActiveProfile } from '../../hooks/useAttachments';

const createModels = (shareCardDefinionList, groupContentsList, shareCardList) => {
  return shareCardList
    .map(card => ShareCardModel.ofData(card, shareCardDefinionList, { groupContents: groupContentsList }))
    .filter(card => !!card);
};

const getShareCard = (props) => {
  const { shareCardId, isCreate, shareCardDefinionList, definitionId, groupContentsList, groupedHealthEntryList, newColorId, shareCardList } = props;

  if (isCreate) {
    const card = ShareCardModel.ofData(
      {
        definition_id: definitionId,
        color_id: newColorId ? Number(newColorId) : undefined,
      },
      shareCardDefinionList,
      { groupContents: groupContentsList },
    );

    if (card) {
      card.init(groupedHealthEntryList);
    }

    return card;
  } else if (shareCardId && shareCardList.length > 0) {
    const foundCards = shareCardList.filter((s) => s.id === shareCardId);
    if (foundCards.length === 0) {
      return null;
    }

    return ShareCardModel.ofData(foundCards[0], shareCardDefinionList, { groupContents: groupContentsList });
  } else {
    return null;
  }
};

interface Props {
  match: any;
  location: any;
}
const ShareCardListView: React.FC<Props> = (props) => {
  const isCreate = props.match.params.id === 'create';
  const shareCardId = !isCreate ? Number(props.match.params.id) : null;
  const searchParams = AppUtils.getQueryParams(props.location.search);
  const newColorId = Number(searchParams.colorId);

  // const isViewing = props.match.url.endsWith('view');
  const definitionId = Number(props.match.params.definitionId);

  const [{ preferredLanguageCode }] = useActiveUser();
  const [{ isActiveProfileMainProfile }] = useUIState();
  const [{ greetingName }] = useActiveProfile();
  const [, navActions] = useNavigate();

  const [{ shareCardList, shareCardDefinionList, ...scState }, scActions] = useShareCardListForActiveProfile();
  const [{ groupedHealthEntryList, ...heState }] = useHealthEntriesForActiveProfile();
  const[{ groupContentsList }] = useGroupShareCardContentForActiveProfile();
  useAttachmentsForActiveProfile();

  const isDataLoaded = scState.isLoaded && heState.isLoaded;
  const scrollElementId = location.hash.slice(1);


  const [state, setState] = React.useState(() => ({
    // shareCardModels: createModels(props),
    originalShareCardData: null,
  }));

  const saveInitialShareCardData = () => {
    const sc = getShareCard({
      shareCardId, isCreate, shareCardDefinionList, definitionId, groupContentsList, groupedHealthEntryList, newColorId, shareCardList,
    });

    if (shareCardId && sc) {
      setState({
        originalShareCardData: JSON.parse(JSON.stringify(sc.data)),
      });
    }
  };

  React.useEffect(() => {
    // const scrollElementId = location.hash.slice(1);
    const scrollElement = document.getElementById(scrollElementId);
    if (!scrollElement) { return; }

    scrollElement.scrollIntoView();
  }, [scrollElementId]);

  React.useEffect(() => {
    if (!shareCardId) {
      // only trigger event if user navigated to share card view (not individual share card view)
      TrackEvent.shareCards.viewedShareCard();
    }
  }, [shareCardId]);

  React.useEffect(() => {
    saveInitialShareCardData();
  }, []);

  const shareCardModels = createModels(shareCardDefinionList, groupContentsList, shareCardList);
  const nonGroupSharecards = shareCardModels.filter(card => !card.isGroupAccessible());
  const groupSharecards = shareCardModels.filter(card => card.isGroupAccessible());
  const phraseDataTitle = AppNavPhrases.shareCard.title(isActiveProfileMainProfile, greetingName);

  return (
    <div className="cmp-sharecard bkgd-color-csAppBGgray" style={{ paddingBottom: 1 }}>
      <ScrollAwareContext>
        <AppNavTopLevel hasDropShadow />

        <ScrollAwareScroller customHeight="auto">
          <AppHeaderScrollableText id={phraseDataTitle.id} values={phraseDataTitle.values} />
          <ContentLoader isContentLoaded={isDataLoaded} blockRenderUntilLoaded isFixed>
            <ShareCardItems
              shareCards={nonGroupSharecards}
              languageCode={preferredLanguageCode}
              collapsibleInfoCard={
                <Card.InfoCollapsibleCard
                  isFlat={true}
                  title={<FormattedMessage id="shareCard.shareCardHelp.heading"/>}
                  subTitle={<FormattedMessage id="shareCard.shareCardHelp.subheading" />}
                  collapsedContent={<GrayTextMarkdown phraseId="shareCard.shareCardHelp.text"/>}
                  className="cmp-sharecard-helpblock"
                />
              }
            />

            <ShareCardItems
              shareCards={groupSharecards}
              languageCode={preferredLanguageCode}
              collapsibleInfoCard={
                <Card.CollapsibleCard
                  isFlat={true}
                  background={COLORS.gsLight}
                  collapsedContent={<GrayTextMarkdown phraseId="shareCard.groupCardHelp.text"/>}
                  className="cmp-sharecard-helpblock"
                >
                  <GeneralPrivacyHeader title={<FormattedMessage id="shareCard.groupCardHelp.heading"/>} subTitle={null} />
                </Card.CollapsibleCard>
              }
            />
          </ContentLoader>

          <TopLevelAddButton.MyShareCards />
        </ScrollAwareScroller>
      </ScrollAwareContext>

      <ShareCardModal
        shareCardId={shareCardId}
        isCreate={isCreate}
        isDataLoaded={isDataLoaded}
        definitionId={definitionId}
        newColorId={newColorId}
        onSubmit={isCreate ? (shareCard, callback) => {
          scActions.apiCreate(shareCard).then((newShareCard: ShareCard) => {
            navActions.goBack(RouteNames.shareCardView(newShareCard.id));
          });
        } : (shareCard, callback) => {
          scActions.apiUpdate(shareCard).then(callback);
        }}
      />
    </div>
  );
};

interface ShareCardItemsProps {
  shareCards: ShareCardModel[];
  collapsibleInfoCard: any;
  languageCode: string;
}
const ShareCardItems: React.FC<ShareCardItemsProps> = (props) => {
  const { shareCards, collapsibleInfoCard, languageCode } = props;

  if (!shareCards.length) return null;

  return (
    <ContentWrapper>
      {collapsibleInfoCard}

      <div className="cmp-sharecard-items">
        {
          shareCards.map((card) => (
            <div key={card.data.id} className="cmp-sharecard-item-wrapper">
              <ShareCardItem
                shareCard={card}
                languageCode={languageCode}
              />
            </div>
          ))
        }
        {
          shareCards.length % 2 === 1 && <div className="cmp-sharecard-item-wrapper" />
        }
      </div>
    </ContentWrapper>
  );
};

export default withRouter(ShareCardListView);
